import React, { useEffect, useState } from 'react';
import { ComponentSkinProps } from '../../../../types/component-skin';
import { CategoryMenuItem } from '../CategoryMenu.types';
import { useLocation } from 'react-router-dom';
import '../CategoryMenu.css';

interface BasicCategoryMenuSkinProps extends ComponentSkinProps {
  componentData?: any;
}

const BasicCategoryMenuSkin: React.FC<BasicCategoryMenuSkinProps> = (props) => {
  const {
    data: {
      menuItems,
      loading,
      error,
      selectedItemId,
      isExpanded,
      breadcrumb,
      currentChildren
    },
    actions: {
      handleMenuItemClick,
      toggleExpanded,
      handleBackToRoot
    },
    utils: { t, navigate, formatDate },
    app
  } = props;
  
  const componentData = props.componentData || {};
  const location = useLocation();
  const [categoryInfo, setCategoryInfo] = useState<any>(null);

  // componentProps에서 설정값 추출
  const {
    showProductCount = false,
    showIcon = false,
    showDescription = false,
    showCategoryInfo = true,
    layout = 'list',
    columns = 3,
    emptyMessage = '메뉴가 없습니다.'
  } = componentData.componentProps || {};

  // 현재 경로에 해당하는 카테고리 정보 확인 - URL 기반 시스템에서는 location.state 사용
  useEffect(() => {
    console.log('✅ CategoryMenu - 현재 경로:', location.pathname);
    console.log('✅ CategoryMenu - location.state:', location.state);
    
    if (location.state && location.state.categoryId !== undefined) {
      console.log('✨ location.state에서 카테고리 정보 발견!');
      setCategoryInfo(location.state);
    } else {
      // location.state가 없으면 루트 상태로 처리
      console.log('⚠️ location.state가 없음 - 루트 상태로 처리');
      setCategoryInfo(null);
    }
  }, [location]);

  // 그리드 레이아웃일 때 컬럼 클래스 계산
  const getGridColumnClass = () => {
    switch (columns) {
      case 2: return 'col-6';
      case 3: return 'col-4';
      case 4: return 'col-3';
      case 6: return 'col-2';
      default: return 'col-4';
    }
  };

  // 로딩 중일 때
  if (loading) {
    return (
      <div className="category-menu-loading">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="sr-only">{t('로딩 중...')}</span>
        </div>
        <span className="ms-2">{t('카테고리를 불러오는 중...')}</span>
      </div>
    );
  }

  // 에러가 있을 때
  if (error) {
    return (
      <div className="category-menu-error alert alert-danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </div>
    );
  }

  // 메뉴 아이템이 없을 때
  if (!menuItems || menuItems.length === 0) {
    return (
      <div className="category-menu-empty">
        {emptyMessage}
      </div>
    );
  }

  // 계층 구조로 메뉴 변환
  const buildMenuTree = (items: CategoryMenuItem[]) => {
    const tree: CategoryMenuItem[] = [];
    const itemMap: { [key: string]: CategoryMenuItem } = {};
    
    // 먼저 모든 아이템을 맵에 저장하고 visible한 것만 필터링
    const visibleItems = items.filter(item => item.visible !== false);
    
    visibleItems.forEach(item => {
      itemMap[item.id] = { ...item, children: [] };
    });
    
    // 부모-자식 관계 설정
    visibleItems.forEach(item => {
      if (item.parentId && itemMap[item.parentId]) {
        itemMap[item.parentId].children!.push(itemMap[item.id]);
      } else {
        tree.push(itemMap[item.id]);
      }
    });
    
    // order 기준으로 정렬
    const sortByOrder = (items: CategoryMenuItem[]) => {
      items.sort((a, b) => (a.order || 0) - (b.order || 0));
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          sortByOrder(item.children);
        }
      });
    };
    
    sortByOrder(tree);
    return tree;
  };

  // 표시할 메뉴 아이템 결정
  const displayItems = (() => {
    console.log('🔍 displayItems 결정 로직:', {
      breadcrumbLength: breadcrumb?.length || 0,
      currentChildrenLength: currentChildren?.length || 0,
      menuItemsLength: menuItems?.length || 0
    });
    
    // breadcrumb이 없으면 루트 레벨 - 웹빌더 설정 메뉴 표시
    if (!breadcrumb || breadcrumb.length === 0) {
      console.log('📋 루트 레벨 - 웹빌더 menuItems 사용');
      return buildMenuTree(menuItems);
    }
    
    // breadcrumb이 있으면 하위 카테고리만 표시 (currentChildren)
    // 최하위 메뉴일 때는 빈 배열 반환 (메뉴 표시 안 함)
    console.log('📋 하위 레벨 - currentChildren 사용:', currentChildren?.length || 0);
    return currentChildren || [];
  })();

  // breadcrumb 렌더링 함수
  const renderBreadcrumb = () => {
    if (!breadcrumb || breadcrumb.length === 0) return null;
    
    return (
      <div className="category-breadcrumb" style={{
        padding: '10px 15px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        marginBottom: '15px',
        fontSize: '14px'
      }}>
        <span 
          style={{ cursor: 'pointer', color: '#1890ff' }}
          onClick={() => {
            // 전체 카테고리로 돌아가기
            handleBackToRoot();
          }}
        >
          전체
        </span>
        {breadcrumb.map((item: {id: number; name: string; parentId: number | null}, index: number) => (
          <span key={`breadcrumb-${item.id}-${index}`}>
            <span style={{ margin: '0 8px', color: '#999' }}>&gt;</span>
            <span
              style={{ 
                cursor: 'pointer', 
                color: index === breadcrumb.length - 1 ? '#333' : '#1890ff',
                fontWeight: index === breadcrumb.length - 1 ? 'bold' : 'normal'
              }}
              onClick={() => {
                if (index < breadcrumb.length - 1) {
                  // 중간 breadcrumb 클릭 시 해당 카테고리로 이동
                  handleMenuItemClick({
                    id: `breadcrumb-cat-${item.id}`,
                    categoryId: item.id,
                    categoryName: item.name,
                    routingPath: `/${item.id}`,
                    parentId: item.parentId ? `breadcrumb-cat-${item.parentId}` : null,
                    order: 0,
                    visible: true
                  });
                }
              }}
            >
              {item.name}
            </span>
          </span>
        ))}
      </div>
    );
  };

  // 리스트 아이템 렌더링 (재귀적)
  const renderListItem = (item: CategoryMenuItem, level: number = 0) => (
    <li 
      key={`basic-list-${item.id}-${level}`} 
      className={`category-menu-item ${selectedItemId === item.id ? 'active' : ''} ${item.children && item.children.length > 0 ? 'has-children' : ''}`}
      style={{ paddingLeft: `${level * 20}px` }}
    >
      <div 
        onClick={() => {
          console.log('🖱️ BasicSkin - 리스트 아이템 클릭됨:', item);
          handleMenuItemClick(item);
        }}
        className="category-menu-link"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleMenuItemClick(item);
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        {showIcon && (
          <i className="category-icon fas fa-folder me-2"></i>
        )}
        <span className="category-name">{item.categoryName}</span>
      </div>
      {showDescription && (
        <p className="category-description">
          라우팅: /shopping{item.routingPath}
        </p>
      )}
      {/* currentChildren이 있으면 하위 메뉴를 표시하지 않음 (한 depth만 보여주기 위해) */}
      {!currentChildren && item.children && item.children.length > 0 && (
        <ul className="category-submenu">
          {item.children.map(child => renderListItem(child, level + 1))}
        </ul>
      )}
    </li>
  );

  // 리스트 레이아웃 렌더링
  const renderListLayout = () => (
    <ul className="category-menu-list">
      {displayItems.map((item: CategoryMenuItem) => renderListItem(item))}
    </ul>
  );

  // 그리드 아이템 렌더링 (플랫하게)
  const flattenMenu = (items: CategoryMenuItem[], level: number = 0): { item: CategoryMenuItem, level: number }[] => {
    const result: { item: CategoryMenuItem, level: number }[] = [];
    items.forEach(item => {
      result.push({ item, level });
      if (item.children && item.children.length > 0) {
        result.push(...flattenMenu(item.children, level + 1));
      }
    });
    return result;
  };

  // 그리드 레이아웃 렌더링
  const renderGridLayout = () => {
    const flatMenuItems = currentChildren ? displayItems.map((item: CategoryMenuItem) => ({ item, level: 0 })) : flattenMenu(displayItems);
    return (
      <div className="category-menu-grid">
        <div className="row">
          {flatMenuItems.map(({ item, level }: { item: CategoryMenuItem; level: number }) => (
            <div key={`basic-grid-${item.id}-${level}`} className={`${getGridColumnClass()} mb-3`}>
              <div 
                className={`category-menu-card h-100 ${selectedItemId === item.id ? 'active' : ''}`}
                onClick={() => {
                  console.log('🖱️ BasicSkin - 그리드 아이템 클릭됨:', item);
                  handleMenuItemClick(item);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleMenuItemClick(item);
                  }
                }}
              >
                <div className="category-card-body">
                  {showIcon && (
                    <div className="category-card-icon">
                      <i className="fas fa-folder"></i>
                    </div>
                  )}
                  <h5 className="category-card-title">
                    {level > 0 && <span className="text-muted">{'└ '.repeat(level)}</span>}
                    {item.categoryName}
                  </h5>
                  {showDescription && (
                    <p className="category-card-description">
                      라우팅: /shopping{item.routingPath}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 드롭다운 아이템 렌더링 (재귀적)
  const renderDropdownItem = (item: CategoryMenuItem, level: number = 0) => (
    <React.Fragment key={`basic-dropdown-${item.id}-${level}`}>
      <div 
        className={`dropdown-item ${selectedItemId === item.id ? 'active' : ''}`}
        style={{ paddingLeft: `${(level * 20) + 16}px`, cursor: 'pointer' }}
        onClick={() => {
          handleMenuItemClick(item);
          toggleExpanded();
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleMenuItemClick(item);
            toggleExpanded();
          }
        }}
      >
        {showIcon && (
          <i className="fas fa-folder me-2"></i>
        )}
        {item.categoryName}
        {showDescription && (
          <span className="ms-2 text-muted">(/shopping{item.routingPath})</span>
        )}
      </div>
      {/* currentChildren이 있으면 하위 메뉴를 표시하지 않음 */}
      {!currentChildren && item.children && item.children.length > 0 && 
        item.children.map(child => renderDropdownItem(child, level + 1))
      }
    </React.Fragment>
  );

  // 드롭다운 레이아웃 렌더링
  const renderDropdownLayout = () => {
    const findSelectedItem = (items: CategoryMenuItem[]): CategoryMenuItem | null => {
      for (const item of items) {
        if (item.id === selectedItemId) return item;
        if (item.children) {
          const found = findSelectedItem(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    const selectedItem = selectedItemId ? findSelectedItem(displayItems) : null;
    
    return (
      <div className="category-menu-dropdown">
        <button 
          className="btn btn-outline-secondary dropdown-toggle w-100"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
        >
          {selectedItem?.categoryName || t('메뉴 선택')}
        </button>
        {isExpanded && (
          <div className="dropdown-menu show w-100">
            {displayItems.map((item: CategoryMenuItem) => renderDropdownItem(item))}
          </div>
        )}
      </div>
    );
  };

  // 레이아웃에 따른 렌더링
  return (
    <div className={`category-menu-container category-menu-${layout}`}>
      {/* Breadcrumb 표시 */}
      {renderBreadcrumb()}
      
      {/* 카테고리 정보 표시 */}
      {showCategoryInfo && (
        <div className="category-info-display" style={{
          padding: '20px',
          marginBottom: '20px',
          backgroundColor: categoryInfo ? '#e6f7ff' : '#fff2e8',
          border: `1px solid ${categoryInfo ? '#91d5ff' : '#ffbb96'}`,
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {categoryInfo ? (
            <>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                color: '#1890ff',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                🎯 현재 선택된 카테고리 정보
              </h4>
              <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.8' }}>
                <div style={{ 
                  padding: '8px 12px', 
                  backgroundColor: '#f0f8ff', 
                  borderRadius: '4px',
                  marginBottom: '8px'
                }}>
                  <strong style={{ color: '#1890ff' }}>카테고리 이름:</strong> 
                  <span style={{ marginLeft: '8px', fontSize: '15px' }}>{categoryInfo.categoryName}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <strong>카테고리 ID:</strong> 
                    <span style={{ color: '#fa541c', marginLeft: '8px' }}>{categoryInfo.categoryId}</span>
                  </div>
                  <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <strong>메뉴 ID:</strong> 
                    <span style={{ color: '#722ed1', marginLeft: '8px' }}>{categoryInfo.menuItemId}</span>
                  </div>
                </div>
                <div style={{ 
                  padding: '8px 12px', 
                  backgroundColor: '#f0f5ff', 
                  borderRadius: '4px',
                  marginTop: '8px'
                }}>
                  <strong>라우팅 경로:</strong> 
                  <code style={{ 
                    color: '#1890ff', 
                    backgroundColor: '#e6f7ff', 
                    padding: '2px 6px', 
                    borderRadius: '3px',
                    marginLeft: '8px',
                    fontSize: '13px'
                  }}>
                    /shopping{categoryInfo.routingPath}
                  </code>
                </div>
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#e6fffb',
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#13c2c2'
                }}>
                  ✓ 카테고리 매핑이 정상적으로 작동하고 있습니다!
                </div>
              </div>
            </>
          ) : (
            <>
              <h4 style={{ 
                margin: '0 0 12px 0', 
                color: '#fa8c16',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                ⚠️ 카테고리 정보 없음
              </h4>
              <div style={{ fontSize: '14px', color: '#666' }}>
                <p style={{ margin: '0 0 8px 0' }}>현재 경로: <code>{location.pathname}</code></p>
                <p style={{ margin: '0', color: '#999', fontSize: '13px' }}>
                  카테고리 메뉴를 클릭하여 라우팅을 테스트해보세요.
                </p>
              </div>
            </>
          )}
        </div>
      )}
      
      {/* 메뉴 렌더링 조건부 표시 */}
      {displayItems && displayItems.length > 0 ? (
        // 표시할 메뉴가 있는 경우
        <>
          {layout === 'list' && renderListLayout()}
          {layout === 'grid' && renderGridLayout()}
          {layout === 'dropdown' && renderDropdownLayout()}
        </>
      ) : breadcrumb && breadcrumb.length > 0 ? (
        // 최하위 메뉴인 경우 (breadcrumb은 있지만 하위 메뉴가 없음)
        <div className="category-no-submenu" style={{
          padding: '20px',
          textAlign: 'center',
          color: '#666',
          fontSize: '14px',
          backgroundColor: '#f9f9f9',
          borderRadius: '4px',
          marginTop: '10px'
        }}>
          더 이상 하위 카테고리가 없습니다.
        </div>
      ) : null}
    </div>
  );
};

export default BasicCategoryMenuSkin;